import React, { useState } from 'react';

import { Flex, VStack, Stack, Checkbox, Tag, Icon, Box, Wrap, WrapItem, useColorModeValue } from '@ecoinvent/ui';
import { FiChevronRight } from 'react-icons/fi';

import { GeographyHierarchy } from 'hooks/data/search/types';

interface NestedCheckboxesProps {
  data: GeographyHierarchy;
  level?: number;
  path?: string;
  onSelect: (path: string, index: string) => void;
  selectedNestedGeographies: Record<string, string>;
  effectivelySelectedCoords: Set<string>;
  isHorizontal?: boolean;
}

const NestedCheckbox = ({
  data,
  level = 1,
  path,
  onSelect,
  selectedNestedGeographies,
  effectivelySelectedCoords,
  isHorizontal = false,
}: NestedCheckboxesProps) => {
  const hoverBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  const iconColor = useColorModeValue('green.500', 'green.50');

  const [isOpen, setIsOpen] = useState<string>('');

  const handleSelect = (item: string, index: string): void => {
    onSelect(item, index);
  };

  const handleStackClick = (name: string): void => {
    isOpen === name ? setIsOpen('') : setIsOpen(name);
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string,
    index: number,
    hasChildren: boolean
  ): void => {
    handleSelect(name, path === undefined ? index.toString() : path + '.' + index);
    if (e.target.checked && hasChildren) setIsOpen(name);
  };

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  if (isHorizontal) {
    return (
      <Wrap spacing={6} py={2}>
        {Object.entries(data)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, item], index) => (
            <WrapItem key={name}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  isChecked={effectivelySelectedCoords.has(path === undefined ? index.toString() : path + '.' + index)}
                  onChange={(e) => handleCheckboxChange(e, name, index, false)}
                >
                  {name}
                </Checkbox>
                <Tag size="sm">{item.count}</Tag>
              </Stack>
            </WrapItem>
          ))}
      </Wrap>
    );
  }

  return (
    <Flex>
      <VStack align="flex-start" minW="200px" py={2}>
        {Object.entries(data)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, data], index) => {
            const hasChildren = Object.keys(data.children || {}).length > 0;

            return (
              <Stack
                key={name}
                paddingY={1}
                paddingX={1}
                direction="row"
                w="100%"
                justifyContent="space-between"
                alignItems="center"
                role="group"
                cursor={hasChildren ? 'pointer' : 'default'}
                _hover={{ bg: hoverBg }}
                onClick={hasChildren ? () => handleStackClick(name) : undefined}
              >
                <Stack onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    isChecked={effectivelySelectedCoords.has(path === undefined ? index.toString() : path + '.' + index)}
                    onChange={(e) => handleCheckboxChange(e, name, index, hasChildren)}
                    fontWeight={Object.values(selectedNestedGeographies).includes(name) || isOpen === name ? 500 : 'normal'}
                    textDecoration={isOpen === name ? 'underline' : 'none'}
                  >
                    {name}
                  </Checkbox>
                </Stack>
                <Tag
                  ml={1}
                  mr="auto"
                  size="sm"
                  _groupHover={{ bg: hasChildren ? 'green.100' : '', color: hasChildren ? 'green.500' : '' }}
                >
                  {data.count}
                </Tag>
                {hasChildren && <Icon as={FiChevronRight} _groupHover={{ color: iconColor }} />}
              </Stack>
            );
          })}
      </VStack>
      <Box borderColor="gray.200" px={2} ml={2}>
        {Object.entries(data)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, item], index) => {
            return isOpen.includes(name) && item.children && Object.keys(item.children).length > 0 ? (
              <NestedCheckbox
                key={name}
                data={item.children}
                level={level + 1}
                path={path === undefined ? index.toString() : path + '.' + index}
                onSelect={onSelect}
                selectedNestedGeographies={selectedNestedGeographies}
                effectivelySelectedCoords={effectivelySelectedCoords}
              />
            ) : null;
          })}
      </Box>
    </Flex>
  );
};

export default NestedCheckbox;
